import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface TalkRatioWidgetProps {
  agentTalkRatio: number;
  isTalkRatioStabilized: boolean;
}

type TalkRatioHealthTone = "light" | "balanced" | "heavy";

function getTalkRatioHealthTone(agentTalkRatio: number): TalkRatioHealthTone {
  if (agentTalkRatio < 0.38) {
    return "light";
  }
  if (agentTalkRatio > 0.72) {
    return "heavy";
  }
  return "balanced";
}

export const TalkRatioWidget: FC<TalkRatioWidgetProps> = (props) => {
  const { isTalkRatioStabilized, agentTalkRatio } = props;
  const healthTone = getTalkRatioHealthTone(props.agentTalkRatio);
  const customerTalkRatio = Math.max(0, Math.min(1, 1 - agentTalkRatio));

  const youBarClass = !isTalkRatioStabilized
    ? "bg-slate-400"
    : healthTone === "balanced"
      ? "bg-emerald-500"
      : healthTone === "heavy"
        ? "bg-gradient-to-r from-orange-500 to-amber-500"
        : "bg-gradient-to-r from-amber-400 to-yellow-400";

  const youLabelClass = !isTalkRatioStabilized
    ? "font-medium text-slate-600"
    : healthTone === "balanced"
      ? "text-emerald-800"
      : healthTone === "heavy"
        ? "font-semibold text-orange-900"
        : "font-semibold text-amber-900";

  const themLabelClass = !isTalkRatioStabilized
    ? "text-muted-foreground"
    : healthTone === "balanced"
      ? "text-slate-600"
      : healthTone === "heavy"
        ? "text-orange-900/85"
        : "text-amber-900/80";

  return (
    <div
      className="flex w-full max-w-[160px] min-w-[100px] flex-col items-start gap-1"
      title={
        !isTalkRatioStabilized
          ? "Early in the call — the split will read more clearly as the conversation continues."
          : healthTone === "balanced"
            ? "Talk ratio looks balanced for discovery."
            : healthTone === "heavy"
              ? "You are taking most of the airtime — make space for the customer."
              : "You have been quiet — step in with a short question or summary."
      }
    >
      <div className="flex h-1.5 w-full max-w-[107px] min-w-[67px] shrink-0 overflow-hidden rounded-full bg-slate-200/90">
        <div
          className={twMerge(
            "h-full transition-[width] duration-1000",
            youBarClass,
          )}
          style={{ width: `${(agentTalkRatio * 100).toFixed(1)}%` }}
        />
        <div
          className="h-full bg-slate-300/88 transition-[width] duration-1000"
          style={{ width: `${(customerTalkRatio * 100).toFixed(1)}%` }}
        />
      </div>
      <p className="w-full text-left text-[10px] leading-tight font-medium tracking-tight">
        <span className={twMerge("tabular-nums", youLabelClass)}>
          {agentTalkRatio.toLocaleString("en-US", {
            maximumFractionDigits: 0,
            style: "percent",
          })}{" "}
          you
        </span>
        <span className="text-slate-400"> • </span>
        <span className={twMerge("tabular-nums", themLabelClass)}>
          {customerTalkRatio.toLocaleString("en-US", {
            maximumFractionDigits: 0,
            style: "percent",
          })}{" "}
          them
        </span>
      </p>
    </div>
  );
};
